import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { DropdownOption, RefreshProjectProps } from "../../types/screensTypes";
import { NewsletterAI, FieldErrorsTranslation } from "../../types/types";
import { checkTranslationInProgress, translateNewsletter, getProjectByTypes, getProjects } from "../../api";
import Dropdown from "../../components/common/Dropdown";
import Icon from "../../components/common/Icon";
import { useGlobalState } from "../../context/useGlobalState";
import Button from "../../components/ui/Button";
import CongratsAI from "../../components/modals/CongratsModal";
import AIGenerationStatusModal from "../../components/modals/AIGenerationStatusModal";
import MultiSelectDropdown from "../../components/common/MultiSelectDropdown";
import { useRemainingTokens } from "../../hooks/useRemainingTokens";
import styles from "./Translation.module.css";

interface NewsletterTranslationProps extends RefreshProjectProps {}

const NewsletterTranslation: React.FC<NewsletterTranslationProps> = ({ refreshProjects, projects }) => {
    const [selectedProject, setSelectedProject] = useState<string | null>(null);
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [selectedNewsletterOption, setSelectedNewsletterOption] = useState<string[]>(["ALL"]);
    const [targetLanguages, setTargetLanguages] = useState<string[]>([]);
    const [projectData, setProjectData] = useState<{ newsletterAI: NewsletterAI[] } | null>(null);
    const [filteredProjects, setFilteredProjects] = useState<{ label: string; value: string }[]>([]);
    const [loadingProject, setLoadingProject] = useState<boolean>(true);
    const [errorProject, setErrorProject] = useState<string | null>(null);
    const token = localStorage.getItem("token");
    const [loadingTranslation, setLoadingTranslation] = useState<boolean>(false);
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [isGenerateButtonDisabled, setIsGenerateButtonDisabled] = useState<boolean>(true);
    const [hasProjectsGenerating, setHasProjectsGenerating] = useState<boolean>(false);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
    const [hasFailedTranslation, setHasFailedTranslation] = useState<boolean>(false);
    const translationStartedAtRef = useRef<number | null>(null);
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const { isTextExhausted, refetch: refetchTokens, data: tokenData } = useRemainingTokens();

    const [fieldErrors, setFieldErrors] = useState<FieldErrorsTranslation>({
        selectedProject: false,
        selectedContentLanguage: false,
        selectedAudioOption: false,
        translations: false,
    });

    const fetchFilteredProjects = async () => {
        const token = localStorage.getItem("token");
        if (!token) return;

        try {
            // Fetch only newsletter projects
            const response = await getProjects({ token, itemType: "newsletter" });
            if (response) {
                const projectOptions = response.map((project: any) => ({
                    label: project.name,
                    value: project.id,
                }));
                setFilteredProjects(projectOptions);
            }
        } catch (error) {
            console.error("Error fetching newsletter projects:", error);
            // Fallback to provided projects or empty array
            setFilteredProjects(projects || []);
        }
    };

    const fetchProject = async (id: string) => {
        const token = localStorage.getItem("token");
        if (!token) return;
        if (!id) return;
        setErrorProject("");
        setLoadingProject(true);

        try {
            // Use the new API to get newsletter content
            const projectResponse = await getProjectByTypes({
                token: token,
                projectId: id,
                targetTypes: ["newsletter"],
            });

            setLoadingProject(false);

            if (!projectResponse.project) {
                setErrorProject("Error loading project");
            } else {
                setProjectData({
                    newsletterAI: projectResponse.project.newsletterAI || [],
                });
            }
        } catch (error) {
            console.error("Error fetching project:", error);
            setLoadingProject(false);
            setErrorProject("Error loading project");
        }
    };

    useEffect(() => {
        const errors: FieldErrorsTranslation = {
            selectedProject: !selectedProject,
            selectedContentLanguage: !selectedContentLanguage,
            selectedAudioOption: !selectedNewsletterOption || selectedNewsletterOption.length === 0,
            translations: targetLanguages.length === 0,
        };

        setFieldErrors(errors);

        const hasErrors = Object.values(errors).some((error) => error);
        setIsGenerateButtonDisabled(hasErrors);
    }, [selectedProject, selectedContentLanguage, selectedNewsletterOption, targetLanguages]);

    useEffect(() => {
        if (isGenerating && !hasProjectsGenerating) {
            if (hasFailedTranslation) {
                setShowErrorModal(true);
            } else {
                setIsModalOpen(true);
            }
            setIsGenerating(false);
            translationStartedAtRef.current = null;
        }
    }, [isGenerating, hasProjectsGenerating, hasFailedTranslation]);

    useEffect(() => {
        const intervalId = setInterval(async () => {
            const token = localStorage.getItem("token");
            if (!token) return;
            const since = translationStartedAtRef.current
                ? new Date(translationStartedAtRef.current).toISOString()
                : undefined;
            const response = await checkTranslationInProgress({ token, since });
            if (!response) {
                console.error("Error checking queue status");
            } else {
                const hadProjectsGenerating = hasProjectsGenerating;
                if (response.queuedItem) {
                    setHasProjectsGenerating(true);
                    setIsGenerating(true);
                } else {
                    if (hadProjectsGenerating && translationStartedAtRef.current) {
                        setHasFailedTranslation(response.hasFailedTranslation ?? false);
                    }
                    setHasProjectsGenerating((prev) => {
                        if (prev) {
                            refetchTokens();
                        }
                        return false;
                    });
                }
            }
        }, 10000); // Check every 10 seconds

        return () => clearInterval(intervalId);
    }, [token, refetchTokens, hasProjectsGenerating]);

    const showErrorMessage = () => {
        setShowErrors(true);
        setTimeout(() => {
            setShowErrors(false);
        }, 3000);
    };

    useEffect(() => {
        fetchFilteredProjects();
        // fetch-on-mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedProject && selectedProject !== "") {
            setSelectedNewsletterOption(["ALL"]);
            fetchProject(selectedProject);
        }
    }, [selectedProject]);

    const filteredNewsletterOptions: DropdownOption[] = [
        { value: "ALL", label: t("all") },
        ...(projectData?.newsletterAI
            ?.filter((newsletter: NewsletterAI) => newsletter.language === selectedContentLanguage)
            // Nothing written means nothing to translate: the backend skips it anyway.
            ?.filter((newsletter: NewsletterAI) => !!(newsletter.text && newsletter.text.trim() !== ""))
            ?.sort((a, b) => a.position - b.position)
            .map((newsletter: NewsletterAI) => ({
                value: newsletter.id,
                label: newsletter.name,
            })) || []),
    ];

    const { data, loading, error } = useGlobalState();

    const languagesAIJsonFormatted: DropdownOption[] =
        data?.languagesAI?.map((language: any) => ({
            value: language.code,
            label: language.label,
        })) || [];

    const getFilteredLanguagesForTranslation = (): DropdownOption[] => {
        return languagesAIJsonFormatted?.filter((language) => language.value !== selectedContentLanguage) || [];
    };

    const generateTranslation = async () => {
        const token = localStorage.getItem("token");
        if (!token) return;

        if (
            !selectedProject ||
            !selectedContentLanguage ||
            !selectedNewsletterOption ||
            selectedNewsletterOption.length === 0 ||
            targetLanguages.length === 0
        ) {
            setShowErrors(true);
            setTimeout(() => {
                setShowErrors(false);
            }, 3000);
            return;
        }

        setLoadingTranslation(true);
        const response = await translateNewsletter({
            token,
            projectId: selectedProject,
            language: selectedContentLanguage,
            selectedOption: selectedNewsletterOption,
            targetLanguages: targetLanguages,
        });
        setLoadingTranslation(false);
        if (!response) {
            console.error("Error generating translations:");
        } else {
            translationStartedAtRef.current = Date.now();
            setHasFailedTranslation(false);
            setHasProjectsGenerating(true);
            setIsGenerating(true);
        }
    };

    const handleNewsletterOptionChange = (selectedOptions: string[]) => {
        const lastSelected = selectedOptions[selectedOptions.length - 1];
        if (lastSelected === "ALL") {
            setSelectedNewsletterOption(["ALL"]);
        } else {
            const updatedOptions = selectedOptions.filter((option) => option !== "ALL");
            setSelectedNewsletterOption(updatedOptions);
        }
    };

    const handleTargetLanguagesChange = (selectedOptions: string[]) => {
        setTargetLanguages(selectedOptions);
    };

    const redirectToProjectUrl = () => {
        if (selectedProject) {
            refreshProjects();
            navigate("/newsletter-projects/" + selectedProject);
            setIsModalOpen(false);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.head}>
                <div className={styles.headTitle}>
                    <h1>{t("newsletterTranslation")}</h1>
                    <p className={styles.sub}>
                        {t("translateYourNewsletters")}
                        <Tooltip title={t("doNotTranslateTooltip")} arrow>
                            <span style={{ display: "inline-flex" }}>
                                <Icon name="ic-info" />
                            </span>
                        </Tooltip>
                    </p>
                </div>
                {tokenData && (
                    <div className={styles.quota}>
                        {t("wordLeftForText")}{" "}
                        <b>{tokenData.remainigTextTokens?.toLocaleString?.() ?? tokenData.remainigTextTokens}</b>
                    </div>
                )}
            </div>

            <div className={styles.scroll}>
                <div className={styles.card}>
                    <h2 className={styles.cardTitle}>
                        <span className={styles.badge}>1</span>
                        {t("whatDoYouWantToTranslate")}
                    </h2>
                    <p className={styles.cardSub}>{t("weCanTranslateAllOrPartOfAProject")}</p>

                    <div
                        className={`${styles.selectsRow}${
                            selectedProject && selectedContentLanguage ? ` ${styles.selectsRowThree}` : ""
                        }`}
                    >
                        <Dropdown
                            label=""
                            value={selectedProject}
                            options={filteredProjects}
                            onChange={setSelectedProject}
                            placeholder={t("selectAProject")}
                            isMissing={showErrors && fieldErrors.selectedProject}
                            loading={false}
                            error={false}
                            isWithoutMargin
                        />
                        <Dropdown
                            label=""
                            value={selectedContentLanguage}
                            options={languagesAIJsonFormatted}
                            onChange={setSelectedContentLanguage}
                            loading={loading}
                            error={error ? true : false}
                            placeholder={t("selectALanguage")}
                            isMissing={showErrors && fieldErrors.selectedContentLanguage}
                            isWithoutMargin
                        />
                        {selectedProject && selectedContentLanguage && (
                            <MultiSelectDropdown
                                label=""
                                options={filteredNewsletterOptions}
                                onChange={handleNewsletterOptionChange}
                                value={selectedNewsletterOption}
                                loading={loadingProject}
                                error={errorProject ? true : false}
                                placeholder={t("selectAnOption")}
                                isMissing={showErrors && fieldErrors.selectedAudioOption}
                                disabled={false}
                                isWithoutMargin
                            />
                        )}
                    </div>
                </div>

                <div className={styles.card}>
                    <h2 className={styles.cardTitle}>
                        <span className={styles.badge}>2</span>
                        {t("languages")}
                    </h2>
                    <p className={styles.cardSub}>{t("whatLanguageWouldYouLikeToTranslateYourProjectTo")}</p>

                    <div className={styles.selectsRow}>
                        <MultiSelectDropdown
                            label=""
                            options={getFilteredLanguagesForTranslation()}
                            onChange={handleTargetLanguagesChange}
                            value={targetLanguages}
                            loading={loading}
                            error={error ? true : false}
                            placeholder={t("selectTargetLanguages")}
                            isMissing={showErrors && fieldErrors.translations}
                            disabled={false}
                            isWithoutMargin
                        />
                    </div>
                </div>

                <Tooltip title={isTextExhausted ? t("quotaReachedTextTooltip") : ""} placement="top">
                    <span>
                        <Button
                            onClick={() => generateTranslation()}
                            label={
                                loadingTranslation || hasProjectsGenerating ? t("generating") : t("startTranslation")
                            }
                            className={styles.startBtn}
                            disabled={isGenerateButtonDisabled || loadingTranslation || isTextExhausted}
                            showError={showErrorMessage}
                        />
                    </span>
                </Tooltip>

                <div className={styles.note}>
                    <b>{t("importantSemicolon")}</b> {t("dependingOnTheVolumeOfText")}
                </div>
            </div>

            <CongratsAI
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                redirectToProjectUrl={redirectToProjectUrl}
                projectId={selectedProject}
                contentType="newsletter"
            />

            <AIGenerationStatusModal
                isOpen={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                status="error"
                rank={null}
                errorTitle={t("audioGenerationFailed")}
                errorSubtitle={t("audioGenerationFailedSubtitle")}
            />
        </div>
    );
};

export default NewsletterTranslation;
